<script setup lang="ts">
    import { onMounted, onUnmounted, useTemplateRef, watch, useSlots } from 'vue';
    import { useRouter } from 'vue-router';
    import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

    const {
        isOpen,
        closeOnRouteChange = true,
        closeOnClickOutside = true,
        closeButton = true,
        fullScreen = false
    } = defineProps<{
        isOpen: boolean;
        closeOnRouteChange?: boolean;
        closeOnClickOutside?: boolean;
        closeButton?: boolean;
        fullScreen?: boolean;
    }>();

    const emit = defineEmits<{ (e: 'close'): void }>();

    defineOptions({
        inheritAttrs: false
    });

    let animationFrameId: number | null = null;
    let clampedBody = false;

    const { clampBody, releaseBody } = useFixedBody();

    const router = useRouter();
    const slots = useSlots();

    const trapTarget = useTemplateRef<HTMLDivElement>('trapTarget');
    const { activate, deactivate } = useFocusTrap(trapTarget, {
        allowOutsideClick: true
    });

    const clampPage = () => {
        clampBody();
        clampedBody = true;
    };

    const releasePage = () => {
        if (!clampedBody) return;

        releaseBody();
        clampedBody = false;
    };

    watch(
        () => isOpen,
        (open) => {
            if (open) {
                clampPage();
                animationFrameId = requestAnimationFrame(() => {
                    animationFrameId = null;
                    activate();
                });
            } else {
                if (animationFrameId != null) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }
                releasePage();
                deactivate();
            }
        },
        { immediate: true }
    );

    watch(
        () => router.currentRoute.value.path,
        () => {
            if (isOpen && closeOnRouteChange) {
                emit('close');
            }
        }
    );

    onMounted(() => {
        document.addEventListener('keydown', handleKeyDownEsc);
    });

    onUnmounted(() => {
        document.removeEventListener('keydown', handleKeyDownEsc);
        if (animationFrameId != null) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }
        deactivate();
        releasePage();
    });

    function handleClickOutside(event: MouseEvent) {
        if (!isOpen || !closeOnClickOutside) return;
        event.stopPropagation();
        event.preventDefault();
        emit('close');
    }

    function handleKeyDownEsc(e: KeyboardEvent) {
        if (!isOpen) return;
        if (e.key === 'Escape') {
            emit('close');
        }
    }
</script>

<template>
    <!-- teleport to body so a transformed or clipping ancestor (e.g. a splide track) cannot
         become the containing block of the fixed backdrop -->
    <Teleport to="body">
        <Transition name="fade-in-out">
            <div
                v-if="isOpen"
                class="backdrop"
                :class="{
                    'dialog-open': isOpen,
                    'full-screen': fullScreen
                }"
                role="dialog"
                aria-modal="true"
                v-bind="$attrs"
                @click="handleClickOutside"
            >
                <div
                    ref="trapTarget"
                    class="wrapper"
                    v-bind="$attrs"
                    @click.stop
                >
                    <div class="modal-content">
                        <div
                            v-if="slots.header"
                            class="modal-content-header"
                        >
                            <slot name="header" />
                        </div>
                        <div class="modal-content-body">
                            <slot
                                name="content"
                                @close="$emit('close')"
                            />
                        </div>
                        <div
                            v-if="slots.footer"
                            class="modal-content-footer"
                        >
                            <slot
                                name="footer"
                                @close="$emit('close')"
                            />
                        </div>
                        <button
                            v-if="closeButton"
                            type="button"
                            class="close-button"
                            @click="$emit('close')"
                            @keydown.space="$emit('close')"
                        >
                            <span class="sr-only">{{ $t('general.close') }}</span>
                            <img
                                src="~/assets/icons/close-white.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style lang="scss" scoped>
    .backdrop {
        position: fixed;
        inset: 0;
        background-color: $gray-transparent;
        z-index: 50;
        padding-block: var(--spacing);
        padding-left: calc(var(--spacing) + var(--safe-area-left));
        padding-right: calc(var(--spacing) + var(--safe-area-right));
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .full-screen {
        // percentages below are relative to this padded area, so the modal stays clear of notch and home indicator
        &.backdrop {
            padding: env(safe-area-inset-top, 0px) var(--safe-area-right) env(safe-area-inset-bottom, 0px)
                var(--safe-area-left);
        }

        .wrapper {
            max-width: 100%;
            max-height: 100%;
            width: 100%;
            height: 100%;
            border-radius: 0;
        }

        @media (min-width: $br-m) {
            // the wrapper's own margin already clears the home indicator; keeps top and bottom symmetric
            &.backdrop {
                padding-block: 0;
            }

            .wrapper {
                max-width: calc(100% - var(--spacing--2));
                max-height: calc(100% - var(--spacing--2));
                width: calc(100% - var(--spacing--2));
                height: calc(100% - var(--spacing--2));
                border-radius: $border-radius;
            }
        }
    }

    .wrapper {
        background-color: $white;
        border-radius: $border-radius;
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 600px;
        max-height: 100%;
        overflow: hidden;
    }

    .close-button {
        position: absolute;
        z-index: 2;
        right: $spacing;
        top: $spacing--05;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: auto;
        padding: $spacing--05;
        background-color: var(--header-mobile-button-color, $black);
        border-radius: 100%;
        cursor: pointer;
        transition:
            background-color $anim-duration-hover $anim-ease,
            transform $anim-duration-hover $anim-ease-bounce;

        &:hover {
            background-color: $color-black-hover;
            transform: $anim-scale-hover;
        }
    }

    .modal-content {
        position: relative;
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;

        .modal-content-body {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
        }

        .modal-content-footer {
            flex: none;
            display: flex;
            padding: $spacing;
        }

        .modal-content-header {
            min-height: 0;
            flex: none;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: $spacing--05;
            padding: $spacing;
        }
    }

    .fade-in-out-enter-active,
    .fade-in-out-leave-active {
        transition: opacity 0.3s ease-in-out;
    }

    .fade-in-out-enter-from,
    .fade-in-out-leave-to {
        opacity: 0;
    }
</style>
